import React from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Stack,
  CircularProgress
} from "@mui/material";
import Wap from "../../Assets/Wap.svg";
import StarsIcon from "@mui/icons-material/Stars";
import StoreIcon from "@mui/icons-material/Store";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { API_URL } from "../../consts";
import "./Profile.css";

const PointsSection = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user);

  const [pointsBalance, setPointsBalance] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);
    axios
      .get(`${API_URL}/points/rewards`)
      .then((res) => {
        if (!isMounted) return;
        const balance = res?.data?.balance ?? userData?.points ?? 0;
        setPointsBalance(balance);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Failed to load points balance", err);
        // Fall back to any balance already known on the user object
        if (typeof userData?.points === "number") {
          setPointsBalance(userData.points);
        } else {
          setError(true);
        }
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [userData]);

  return (
    <Card elevation={1} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
          <StarsIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Points & Rewards
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
          <Avatar 
            src={Wap} 
            sx={{ 
              width: { xs: 80, sm: 100 }, 
              height: { xs: 80, sm: 100 }, 
              mx: 'auto',
              mb: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }} 
          />
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Unlock Amazing Rewards
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1 }}>
            {loading ? (
              <Chip
                icon={<CircularProgress size={16} color="inherit" />}
                label="Loading balance…"
                color="primary"
                variant="outlined"
              />
            ) : error ? (
              <Chip
                icon={<StarsIcon />}
                label="Balance unavailable"
                color="default"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<StarsIcon />}
                label={`${pointsBalance} pts`}
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>
          <Typography 
            variant="body1" 
            color="textSecondary" 
            sx={{ 
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Use your points to access premium features, exclusive content, and special perks
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Button
            variant="contained"
            onClick={() => navigate("/points-market")}
            startIcon={<StoreIcon />}
            sx={{ 
              py: { xs: 1.2, sm: 1.5 },
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                color: 'white',
              }
            }}
            fullWidth
          >
            Visit Points Market
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<HelpOutlineIcon />}
            sx={{ 
              py: { xs: 1.2, sm: 1.5 },
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white'
              }
            }}
            fullWidth
            onClick={() => navigate("/earn-points")}
          >
            How to Earn Points?
          </Button>
        </Stack>

        <Box sx={{ 
          mt: { xs: 2, sm: 3 }, 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: 'grey.50', 
          borderRadius: 2 
        }}>
          <Typography 
            variant="body2" 
            color="textSecondary" 
            sx={{ 
              textAlign: 'center',
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            💡 <strong>Tip:</strong> Complete daily tasks, participate in discussions, and invite friends to earn more points!
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PointsSection;
